// Session + redirect helpers for the /plans checkout flow.
//
// The agency panel sends users here as /plans/:id?token=<jwt>&returnUrl=<panel>.
// The token is moved into localStorage straight away (and scrubbed from the
// address bar); the returnUrl is kept in sessionStorage so it survives the
// "Back to All Plans" hop.

export const TOKEN_KEY = "auth-token";
const RETURN_KEY = "mb_plans_return_url";
export const DEFAULT_PANEL_URL = "https://post.magicbolt.in";

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const getToken = () => safe(() => localStorage.getItem(TOKEN_KEY));
export const setToken = (t) => safe(() => localStorage.setItem(TOKEN_KEY, t));
export const clearToken = () => safe(() => localStorage.removeItem(TOKEN_KEY));

// Only redirect users (and their token!) to our own panel hosts, so a crafted
// ?returnUrl= can't turn this page into an open redirect / token leak.
export function isAllowedReturnUrl(raw) {
  let u;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  const host = u.hostname.toLowerCase();
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (isLocal) return process.env.NODE_ENV !== "production";
  if (u.protocol !== "https:") return false;
  return host === "magicbolt.in" || host.endsWith(".magicbolt.in");
}

export function getReturnUrl() {
  const stored = safe(() => sessionStorage.getItem(RETURN_KEY));
  return stored && isAllowedReturnUrl(stored) ? stored : null;
}

// Reads ?token= / ?returnUrl= from a query string, persists them, and returns
// true when the URL carried anything so the caller can scrub it.
export function captureSessionFromSearch(search) {
  const params = new URLSearchParams(search);
  const token = params.get("token");
  const returnUrl = params.get("returnUrl");
  if (token) setToken(token);
  if (returnUrl && isAllowedReturnUrl(returnUrl)) {
    safe(() => sessionStorage.setItem(RETURN_KEY, returnUrl));
  }
  return Boolean(token || returnUrl);
}

// Panel return (Stories 1 & 2) adds planSubscribed=true. A guest who just
// signed up (Story 3) has no returnUrl and lands on the default panel with
// only the token.
export function buildRedirectUrl({ token, returnUrl, subscribed }) {
  const fromPanel = Boolean(returnUrl) && isAllowedReturnUrl(returnUrl);
  const url = new URL(fromPanel ? returnUrl : DEFAULT_PANEL_URL);
  if (token) url.searchParams.set("token", token);
  if (fromPanel && subscribed) url.searchParams.set("planSubscribed", "true");
  return url.toString();
}
